import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Trophy, Award, CheckCircle, Clock, Play, Target,
  BookOpen, MessageSquare, FileText, Flame, FolderOpen, 
  Heart, Star, Users, Calendar, Sparkles
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import confetti from 'canvas-confetti';


import { BACKEND_URL } from '../../config/api';
const API = BACKEND_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

const CHALLENGE_ICONS = {
  'profile': { icon: Users, color: 'from-blue-400 to-blue-600' },
  'course': { icon: BookOpen, color: 'from-green-400 to-green-600' },
  'quiz': { icon: Target, color: 'from-blue-400 to-blue-600' },
  'community': { icon: MessageSquare, color: 'from-blue-400 to-blue-600' },
  'assignment': { icon: FileText, color: 'from-blue-400 to-blue-600' },
  'streak': { icon: Flame, color: 'from-blue-400 to-blue-600' },
  'project': { icon: FolderOpen, color: 'from-blue-400 to-blue-600' },
  'behaviour': { icon: Heart, color: 'from-blue-400 to-blue-600' },
  'special': { icon: Star, color: 'from-blue-400 to-blue-600' }
};

const MOTIVATIONAL_PHRASES = [
  "You're doing great!",
  "Keep pushing towards your goal!",
  "Every challenge makes you stronger!",
  "You've got this!",
  "Progress is progress, no matter how small!",
  "Your dedication is inspiring!",
  "One step closer to greatness!"
];

export default function ChallengeCards({ challenges, onChallengeUpdate, showAll = false }) {
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [completedChallenge, setCompletedChallenge] = useState(null);

  // Auto-check is now handled at page load level (Dashboard/Challenges page)
  // No need to check here anymore

  const getMotivationalPhrase = () => {
    return MOTIVATIONAL_PHRASES[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.length)];
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'completed':
        return {
          label: 'Completed',
          color: 'bg-green-100 text-green-800',
          icon: CheckCircle,
          progress: 100
        };
      case 'in_progress':
        return {
          label: 'In Progress',
          color: 'bg-yellow-100 text-yellow-800',
          icon: Clock,
          progress: 50
        };
      default:
        return {
          label: 'Not Started',
          color: 'bg-gray-100 text-gray-800',
          icon: Play,
          progress: 0
        };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExpired = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  const startChallenge = async (challengeId) => {
    try {
      await axios.put(`${API}/api/student-challenges/${challengeId}/start`, {}, { headers: getAuthHeaders() });
      toast.success('Challenge started! Good luck!');
      onChallengeUpdate();
    } catch (error) {
      console.error('Failed to start challenge:', error);
      toast.error('Failed to start challenge');
    }
  };

  const triggerCelebration = (challenge) => {
    // Confetti animation - Set high z-index to appear in front
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti(Object.assign({}, defaults, {
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      }));
      confetti(Object.assign({}, defaults, {
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      }));
    }, 250);

    setCompletedChallenge(challenge);
    setShowCelebration(true);
  };

  const displayedChallenges = showAll ? challenges : challenges.slice(0, 3);

  if (challenges.length === 0) {
    return (
      <Card className="border-0 card-shadow bg-white rounded-2xl">
        <CardContent className="p-8 text-center">
          <Trophy className="w-16 h-16 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
          <h3 className="text-lg font-bold text-navy mb-2">No Challenges Available</h3>
          <p className="text-[#4D4D4D] font-light">Check back soon for exciting challenges!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {displayedChallenges.map(({ challenge, completion }) => {
        const isCompleted = completion.status === 'completed';
        const isInProgress = completion.status === 'in_progress';
        const challengeIcon = CHALLENGE_ICONS[challenge.challenge_type] || CHALLENGE_ICONS.special;
        const IconComponent = challengeIcon.icon;
        const expired = isExpired(challenge.deadline);
        
        // Background color based on status - Light blue for incomplete, Green for completed
        const bgColor = isCompleted ? 'bg-green-50' : 'bg-[#E3F2FD]';
        const borderColor = isCompleted ? 'border-green-200' : 'border-[#90CAF9]';

        return (
          <Card 
            key={challenge.id}
            className={`border ${borderColor} card-shadow hover:shadow-lg transition-all duration-300 rounded-2xl overflow-hidden ${bgColor}
              ${expired && !isCompleted ? 'opacity-75' : ''}
            `}
          >
            <CardContent className="p-5">
              {/* Header with Title - Removed Icon */}
              <div className="mb-3">
                <h3 className="font-bold text-base text-navy line-clamp-2 mb-1">{challenge.title}</h3>
                <p className="text-xs text-[#4D4D4D] capitalize">{challenge.challenge_type.replace('_', ' ')}</p>
              </div>

              {/* Description */}
              <p className="text-sm text-[#4D4D4D] line-clamp-3 mb-4">{challenge.description}</p>

              {/* Deadline if exists */}
              {challenge.deadline && (
                <div className={`flex items-center space-x-2 text-xs mb-3 px-3 py-2 rounded-lg ${expired ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-[#4D4D4D]'}`}>
                  <Calendar className="w-3 h-3" />
                  <span>{expired ? 'Expired' : 'Due'}: {formatDate(challenge.deadline)}</span>
                </div>
              )}

              {/* Completion Message */}
              {isCompleted && (
                <div className="bg-green-100 px-3 py-2 rounded-lg mb-3">
                  <p className="text-sm text-green-800 font-medium">✅ Completed! +{completion.gp_awarded} GP earned</p>
                </div>
              )}

              {/* Action Button */}
              <div className="pt-2">
                {!isCompleted && !expired && (
                  <Button
                    onClick={() => setSelectedChallenge(challenge)}
                    variant="outline"
                    className="w-full border-2 border-primary-blue text-primary-blue hover:bg-primary-blue hover:text-white font-medium py-2 rounded-xl transition-smooth"
                  >
                    View Details
                  </Button>
                )}
                
                {isCompleted && (
                  <Button
                    onClick={() => triggerCelebration(challenge)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 rounded-xl transition-smooth"
                  >
                    Celebrate Together
                  </Button>
                )}

                {expired && !isCompleted && (
                  <Button
                    disabled
                    className="w-full bg-gray-300 text-gray-600 font-medium py-2 rounded-xl cursor-not-allowed"
                  >
                    Expired
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Challenge Details Modal */}
      {selectedChallenge && (
        <Dialog open={true} onOpenChange={() => setSelectedChallenge(null)}>
          <DialogContent className="max-w-2xl bg-white rounded-2xl border-0 shadow-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-navy flex items-center space-x-3">
                <Trophy className="w-6 h-6 text-yellow-500" strokeWidth={2} />
                <span>{selectedChallenge.title}</span>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl">
                <h3 className="font-bold text-navy mb-3">Challenge Description</h3>
                <p className="text-[#4D4D4D] leading-relaxed">{selectedChallenge.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-yellow-50 rounded-xl">
                  <Award className="w-8 h-8 text-yellow-500 mx-auto mb-2" strokeWidth={2} />
                  <div className="font-bold text-yellow-700">{selectedChallenge.gp_reward} GP</div>
                  <div className="text-xs text-yellow-600">Reward</div>
                </div>

                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <Target className="w-8 h-8 text-blue-500 mx-auto mb-2" strokeWidth={2} />
                  <div className="font-bold text-blue-700 capitalize">{selectedChallenge.challenge_type}</div>
                  <div className="text-xs text-blue-600">Type</div>
                </div>

                {selectedChallenge.deadline && (
                  <div className="text-center p-4 bg-red-50 rounded-xl">
                    <Calendar className="w-8 h-8 text-red-500 mx-auto mb-2" strokeWidth={2} />
                    <div className="font-bold text-red-700">{formatDate(selectedChallenge.deadline)}</div>
                    <div className="text-xs text-red-600">Deadline</div>
                  </div>
                )}
              </div>

              {/* Requirements based on challenge type */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <h4 className="font-bold text-navy mb-2">Requirements:</h4>
                <p className="text-sm text-[#4D4D4D]">
                  Complete the required actions for this {selectedChallenge.challenge_type.replace('_', ' ')} challenge to earn your GP reward!
                </p>
              </div>

              <div className="text-center">
                <Button
                  onClick={() => setSelectedChallenge(null)}
                  className="bg-primary-blue hover:bg-[#1874CD] text-white font-semibold px-8 py-3 rounded-xl transition-smooth"
                >
                  Got it!
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Celebration Modal */}
      {showCelebration && completedChallenge && (
        <Dialog open={true} onOpenChange={() => setShowCelebration(false)}>
          <DialogContent className="max-w-md bg-white rounded-2xl border-0 shadow-2xl text-center">
            <div className="space-y-6 py-4">
              <div className="relative">
                <div className="w-24 h-24 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mx-auto flex items-center justify-center">
                  <Trophy className="w-12 h-12 text-white" strokeWidth={2} />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" strokeWidth={2} />
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-navy mb-2">🎉 Congratulations!</h3>
                <p className="text-lg font-medium text-green-600">Challenge Completed!</p>
                <p className="text-sm text-[#4D4D4D] mt-2">{completedChallenge.title}</p>
              </div>

              <div className="bg-yellow-50 p-4 rounded-xl">
                <div className="flex items-center justify-center space-x-2 text-yellow-700">
                  <Award className="w-5 h-5" strokeWidth={2} />
                  <span className="font-bold text-lg">+{completedChallenge.gp_reward} GP Earned!</span>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-xl">
                <p className="text-blue-800 font-medium">🌟 {getMotivationalPhrase()}</p>
              </div>

              <Button
                onClick={() => setShowCelebration(false)}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-3 rounded-xl transition-smooth"
              >
                Awesome! Continue Learning
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}